/* ============================================================================
   Slide 7b — "Mind & Body" — SHARED CONTENT (single source of truth)
   Read by both MindAndBodyInner (desktop) and HTLDSlide7bMobile. Edit copy ONLY
   here — English and German side by side; the German object must have exactly
   the same shape as the English one.
   ========================================================================== */
import { ByLang, useL } from "i18n/lang";

export const accent7b = "#c090e0";

export interface Seg { t: string; b?: boolean; em?: boolean; }

const en = {
  meta: {
    breadcrumbPre: "Conscious Sleep",
    breadcrumbAccent: "Mind Awake, Body Asleep",
    headerTitlePlain: "Mind ",
    headerTitleAccent: "& Body",
    titlePre: "The Body Sleeps, ",
    titleAccentWord: "Awareness Stays",
    titlePost: "",
    subtitle: "What rests, and what remains awake, at the edge of sleep",
    stillLabel: "The body grows still",
    awarenessLabel: "Awareness need not follow",
    watchLabel: "Watch, do not grasp",
  },
  still: [
    { t: "As you cross into dreaming sleep, the body settles into a deep, natural stillness — the muscles soften and let go almost entirely. This is the body's own " },
    { t: "protective rest:", b: true },
    { t: " it keeps you still so that the vivid activity of the dreaming mind stays inward, and the resting body is never disturbed. It happens every night, on its own." },
  ] as Seg[],
  awareness: [
    { t: "Ordinarily, awareness dissolves the moment the body lets go — we simply fall asleep. The practice is to let the body soften completely while a " },
    { t: "quiet thread of awareness", b: true },
    { t: " stays present — resting within the stillness rather than disappearing into it. The body sleeps; the mind stays gently awake." },
  ] as Seg[],
  watch: [
    { t: "As images and dreams begin to arise, the art is to " },
    { t: "watch — a witness, not a participant.", b: true },
    { t: " Not chasing the images, not swept into the story. Simply observing, steady and at ease. Remaining at that edge — body asleep, mind awake — is where Dreamfulness begins." },
  ] as Seg[],
};

export type Slide7bContent = typeof en;

const de: Slide7bContent = {
  meta: {
    breadcrumbPre: "Bewusster Schlaf",
    breadcrumbAccent: "Geist wach, Körper schläft",
    headerTitlePlain: "Geist ",
    headerTitleAccent: "& Körper",
    titlePre: "Der Körper schläft, ",
    titleAccentWord: "das Gewahrsein bleibt",
    titlePost: "",
    subtitle: "Was ruht und was wach bleibt – an der Schwelle zum Schlaf",
    stillLabel: "Der Körper wird still",
    awarenessLabel: "Das Gewahrsein muss nicht folgen",
    watchLabel: "Beobachten, nicht festhalten",
  },
  still: [
    { t: "Wenn du in den Traumschlaf hinübergleitest, sinkt der Körper in eine tiefe, natürliche Stille – die Muskeln werden weich und lassen fast ganz los. Das ist die " },
    { t: "schützende Ruhe", b: true },
    { t: " des Körpers: Sie hält dich still, damit das lebhafte Geschehen des träumenden Geistes nach innen gerichtet bleibt und der ruhende Körper ungestört ist. Das geschieht jede Nacht, ganz von selbst." },
  ],
  awareness: [
    { t: "Normalerweise löst sich das Gewahrsein auf, sobald der Körper loslässt – wir schlafen einfach ein. In der Praxis lässt du den Körper ganz weich werden, während ein " },
    { t: "leiser Faden des Gewahrseins", b: true },
    { t: " bestehen bleibt – er ruht in der Stille, statt darin zu verschwinden. Der Körper schläft; der Geist bleibt sanft wach." },
  ],
  watch: [
    { t: "Wenn Bilder und Träume aufsteigen, liegt die Kunst darin, " },
    { t: "zu beobachten – als Zeuge, nicht als Mitspieler.", b: true },
    { t: " Den Bildern nicht nachjagen, nicht in die Geschichte hineingezogen werden. Einfach beobachten, ruhig und gelassen. An dieser Schwelle zu verweilen – Körper schläft, Geist wach – ist der Beginn von Dreamfulness." },
  ],
};

export const slide7bContent: ByLang<Slide7bContent> = { en, de };
export const useSlide7b = () => useL(slide7bContent);
